// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, MapPin, Ticket, Plus, Minus } from 'lucide-react';
import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/formatters';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface SpectatorTicket {
  id: string;
  name: string;
  priceInCents: number; // Original price in cents from DB
  priceDisplay: number; // For display ($)
  description?: string;
  is_active: boolean;
  thumbnail_url?: string;
}

interface EventData {
  id: string;
  name: string;
  slug: string;
  start_date: string;
  end_date?: string;
  venue_name?: string;
  venue_city?: string;
  venue_state?: string;
  spectator_tickets: SpectatorTicket[];
}

interface TicketSelection {
  ticket: SpectatorTicket;
  quantity: number;
}

interface ContactInfo {
  name: string;
  email: string;
  phone: string;
}

const SpectatorTicketPurchase: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [ticketSelections, setTicketSelections] = useState<Map<string, number>>(new Map());
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    name: '',
    email: '',
    phone: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchEventAndTickets();
    }
  }, [eventId]);

  const fetchEventAndTickets = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('events')
        .select(`
          id,
          name,
          slug,
          start_date,
          end_date,
          venue_name,
          venue_city,
          venue_state,
          event_pricing:event_pricing(pricing_structure)
        `)
        .eq('id', eventId)
        .single();

      if (error) throw error;
      if (!data) {
        toast.error('Event not found');
        navigate('/events');
        return;
      }

      // Extract spectator tickets from pricing structure
      const pricingStructure = Array.isArray(data.event_pricing) && data.event_pricing.length > 0
        ? data.event_pricing[0].pricing_structure
        : {};

      const spectatorTickets = (pricingStructure.spectator_tickets || [])
        .filter((t: any) => t.is_active)
        .map((t: any) => ({
          id: t.id || `ticket-${t.name}`,
          name: t.name,
          priceInCents: t.price || 0, // Original price in cents from DB
          priceDisplay: (t.price || 0) / 100, // For display
          description: t.description,
          is_active: t.is_active,
          thumbnail_url: t.thumbnail_url
        }));

      if (spectatorTickets.length === 0) {
        toast.error('No spectator tickets available for this event');
        navigate(`/events/${data.slug}`);
        return;
      }

      setEvent({
        id: data.id,
        name: data.name,
        slug: data.slug,
        start_date: data.start_date,
        end_date: data.end_date,
        venue_name: data.venue_name,
        venue_city: data.venue_city,
        venue_state: data.venue_state,
        spectator_tickets: spectatorTickets
      });
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (ticketId: string, delta: number) => {
    setTicketSelections(prev => {
      const newSelections = new Map(prev);
      const currentQty = newSelections.get(ticketId) || 0;
      const newQty = Math.max(0, currentQty + delta);
      
      if (newQty === 0) {
        newSelections.delete(ticketId);
      } else {
        newSelections.set(ticketId, newQty);
      }
      
      return newSelections;
    });
  };

  const calculateTotal = () => {
    if (!event) return 0;
    
    let total = 0;
    ticketSelections.forEach((quantity, ticketId) => {
      const ticket = event.spectator_tickets.find(t => t.id === ticketId);
      if (ticket) {
        total += ticket.priceInCents * quantity;
      }
    });
    
    return total;
  };

  const getSelectedTickets = (): TicketSelection[] => {
    if (!event) return [];
    
    const selected: TicketSelection[] = [];
    ticketSelections.forEach((quantity, ticketId) => {
      const ticket = event.spectator_tickets.find(t => t.id === ticketId);
      if (ticket && quantity > 0) {
        selected.push({ ticket, quantity });
      }
    });
    
    return selected;
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseLocalDate(dateString), 'MMMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const handleCheckout = async () => {
    const selectedTickets = getSelectedTickets();
    
    if (selectedTickets.length === 0) {
      toast.error('Please select at least one ticket');
      return;
    }
    
    if (!contactInfo.name || !contactInfo.email || !contactInfo.phone) {
      toast.error('Please fill in all contact information');
      return;
    }

    setIsProcessing(true);
    
    try {
      const { data, error } = await invokeEdgeFunction('create-spectator-checkout', {
        body: {
          event_id: eventId,
          tickets: selectedTickets.map(({ ticket, quantity }) => ({
            name: ticket.name,
            price: ticket.priceInCents,
            quantity: quantity
          })),
          contact: {
            name: contactInfo.name,
            email: contactInfo.email,
            phone: contactInfo.phone
          },
          success_url: `${window.location.origin}/spectator-ticket-success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: window.location.href
        }
      });

      if (error) throw error;
      
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
      
    } catch (err: any) {
      console.error('Checkout error:', err);
      toast.error(err.message || 'Failed to start checkout');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <div className="text-center">Loading event details...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <div className="text-center">Event not found</div>
      </div>
    );
  }

  const selectedTickets = getSelectedTickets();
  const totalAmount = calculateTotal();

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(`/events/${event.slug}`)}
        className="mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Event
      </Button>

      {/* Event Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Spectator Tickets</h1>
        <h2 className="text-2xl text-muted-foreground mb-4">{event.name}</h2>
        
        <div className="flex flex-col gap-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(event.start_date)}</span>
            {event.end_date && event.end_date !== event.start_date && (
              <span> - {formatDate(event.end_date)}</span>
            )}
          </div>
          
          {event.venue_name && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>
                {event.venue_name}
                {event.venue_city && `, ${event.venue_city}`}
                {event.venue_state && `, ${event.venue_state}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Ticket Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Select Tickets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {event.spectator_tickets.map(ticket => {
            const quantity = ticketSelections.get(ticket.id) || 0;
            
            return (
              <div
                key={ticket.id}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 border rounded-lg"
              >
                <div className="flex-1">
                  <h3 className="font-semibold">{ticket.name}</h3>
                  {ticket.description && (
                    <p className="text-sm text-muted-foreground mt-1">{ticket.description}</p>
                  )}
                  <p className="text-lg font-bold text-primary mt-2">
                    {formatPrice(ticket.priceInCents)}
                  </p>
                </div>
                
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(ticket.id, -1)}
                    disabled={quantity === 0}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  
                  <span className="w-12 text-center font-semibold text-lg">
                    {quantity}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(ticket.id, 1)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={contactInfo.name}
              onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="your.email@example.com"
              value={contactInfo.email}
              onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={contactInfo.phone}
              onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Order Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Order Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedTickets.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No tickets selected yet
            </p>
          ) : (
            <>
              <div className="space-y-3">
                {selectedTickets.map(({ ticket, quantity }) => (
                  <div key={ticket.id} className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{ticket.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatPrice(ticket.priceInCents)} × {quantity}
                      </p>
                    </div>
                    <p className="font-semibold">
                      {formatPrice(ticket.priceInCents * quantity)}
                    </p>
                  </div>
                ))}
              </div>
              
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Total</span>
                  <span className="text-2xl text-primary">
                    {formatPrice(totalAmount)}
                  </span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Checkout Button */}
      <div className="flex justify-end">
        <Button
          size="lg"
          onClick={handleCheckout}
          disabled={isProcessing || calculateTotal() === 0}
          className="w-full"
        >
          {isProcessing ? 'Processing...' : `Checkout - ${formatPrice(calculateTotal())}`}
        </Button>
      </div>
    </div>
  );
};

export default SpectatorTicketPurchase;